import type {User} from "account/User";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {minorToMajor} from "core/format/minorUnits";
import {planOf} from "home/gating";
import type {BillingPeriod, PaidPlanId} from "pricing/planCatalogue";
import type {CouponGift, RegularPriceQuote} from "offers/ladder/currentLadderOffer";
import {resumeFreeCheckout, type FreeCheckoutPorts} from "offers/resumeFreeCheckout";

/**
 * What a limit-wall email link is worth on `/pricing` (kitecorp/transferify-api-service#2206).
 *
 * About an hour after a Free sender meets the hosted size wall or the Instant wall and does not pay,
 * the api mails one coupon for the plan that fits what they tried to send. Its link goes to the api,
 * which records the click and redirects to `/pricing?wall=<token>`. This module owns every rule the
 * page then applies, and imports no React.
 *
 * <b>The token is a bearer credential and is never persisted</b>, for the ladder's reasons
 * (`offers/ladder/ladderOffer`): it authorizes one account's discount, so it lives in the address bar
 * and in component state only.
 *
 * <b>Every figure is the api's.</b> The api resolves the live code, the first invoice and the renewal in
 * the reader's currency at the moment the page asks; nothing here recomputes them from the baked
 * catalogue, and nothing here judges the currency, because the checkout's `/plan` read picks the price
 * by the same edge-country-first order the resolve answered on.
 *
 * <b>The surface is SERVER-witnessed</b> (`UpsellMoment.LIMIT_WALL_EMAIL`): the api records clicked,
 * checkout_opened and paid itself, so the browser reports no funnel step for it
 * (see `clientReportsCheckoutStep` in `offers/ladder/ladderOffer`).
 */

/** The query parameter the api's redirect carries, mirroring `LimitWallOfferService.LINK_PARAMETER`. */
export const LIMIT_WALL_PARAMETER = "wall";

/** Every minted token starts with this, mirroring `LimitWallOffers.TOKEN_PREFIX`. */
export const LIMIT_WALL_TOKEN_PREFIX = "wall.";

/** The widest token the api will look up, mirroring `LimitWallOffers.MAX_TOKEN_LENGTH`. */
export const LIMIT_WALL_TOKEN_MAX_LENGTH = 64;

/** `LimitWallOfferResponse`: what the link offers right now. Amounts are minor units in `currency`. */
export interface LimitWallOffer {
	targetPlan: "FREE" | "BASIC" | "PLUS" | "PREMIUM";
	/** The link's 72 hours are up; every other field is then absent. */
	expired: boolean;
	currency?: string | null;
	percentOff?: number | null;
	amountOff?: number | null;
	firstMonth?: number | null;
	renewal?: number | null;
	/** 0 means checkout is at the full price (owner decision C7). */
	discountsLeftToday: number;
	maxHostedUploadBytes?: number | null;
}

/*
 * Compile-time binding to the generated spec: a renamed or retyped field fails `tsc -b` here, naming the
 * field, rather than the dialog quoting a figure it misread.
 */
export type _LimitWallOffer = AssertNoSpecDrift<SpecDrift<LimitWallOffer, Schemas["LimitWallOfferResponse"]>>;

/**
 * What asking the api produced. `refused` is the api saying the token names no send (404) or is
 * malformed (400); `unavailable` is our own transport failing, which says nothing about the link.
 */
export type LimitWallResolution =
	| {status: "offer"; offer: LimitWallOffer}
	| {status: "refused"}
	| {status: "unavailable"};

/**
 * What `/pricing` shows for the link that was followed.
 *
 * - `none`: the ordinary page, nothing to say.
 * - `expired`: the ladder's expired notice above the ordinary page.
 * - `coupon`: the claim dialog with the api's coupon. `withdrawn` is a coupon the till refused for this
 *   reader while the api still calls it live (another account's link, or a paid plan since the email):
 *   the dialog says the offer is gone and never buys at another price on its own.
 * - `regular`: the claim dialog at the plan's full price, with the note that today's offers are used up.
 */
export type LimitWallPresentation =
	| {kind: "none"}
	| {kind: "expired"}
	| {kind: "coupon"; gift: CouponGift; discountsLeft: number; withdrawn: boolean}
	| {kind: "regular"; quote: RegularPriceQuote};

/** The token out of a `location.search`-style string, or null when there is none this page may ask about. */
export function parseLimitWallToken(search: string): string | null {
	const value = new URLSearchParams(search.replace(/^\?/, "")).get(LIMIT_WALL_PARAMETER)?.trim();
	if (value === undefined || !value.startsWith(LIMIT_WALL_TOKEN_PREFIX)
		|| value.length === LIMIT_WALL_TOKEN_PREFIX.length || value.length > LIMIT_WALL_TOKEN_MAX_LENGTH) {
		return null;
	}
	return value;
}

/** The same search with the token removed, for the expired path; every other parameter stays. */
export function withoutLimitWallToken(search: string): string {
	const params = new URLSearchParams(search.replace(/^\?/, ""));
	params.delete(LIMIT_WALL_PARAMETER);
	const rest = params.toString();
	return rest.length === 0 ? "" : `?${rest}`;
}

const PAID_TARGETS: Record<LimitWallOffer["targetPlan"], PaidPlanId | null> = {
	FREE: null, BASIC: "basic", PLUS: "plus", PREMIUM: "premium",
};

const isWhole = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value);

/**
 * What the page shows, from what the api answered and whether the till has already refused the token.
 *
 * The order is the rule. A refusal or an expired link comes first and never reveals a price. Then
 * `discountsLeftToday`: 0 is the full price (C7) whatever else rides along, because the api's schema says
 * 0 means checkout is at the full price. Only then is a coupon read, and only one whose every figure can
 * be stated honestly; anything else is the ordinary page rather than a guess.
 *
 * <b>After a refusal the reader is never left with nothing.</b> The api refused the token at the till, so
 * a re-read that cannot state the full price or a still-live coupon is the expired notice: nothing was
 * charged, and the notice says the reader can still subscribe at the prices below.
 */
export function limitWallPresentation(
	resolution: LimitWallResolution | null, refusedAtTill: boolean,
): LimitWallPresentation {
	const answered = presentationOf(resolution);
	if (!refusedAtTill) return answered;
	if (answered.kind === "coupon") return {...answered, withdrawn: true};
	return answered.kind === "none" ? {kind: "expired"} : answered;
}

function presentationOf(resolution: LimitWallResolution | null): LimitWallPresentation {
	if (resolution === null || resolution.status === "unavailable") return {kind: "none"};
	if (resolution.status === "refused" || resolution.offer.expired) return {kind: "expired"};
	const {targetPlan, currency, percentOff, amountOff, firstMonth, renewal, discountsLeftToday, maxHostedUploadBytes} = resolution.offer;
	const plan = PAID_TARGETS[targetPlan];
	if (plan === null || typeof currency !== "string" || currency.length === 0 || !isWhole(renewal) || renewal <= 0
		|| !isWhole(discountsLeftToday) || discountsLeftToday < 0
		|| (maxHostedUploadBytes != null && (!isWhole(maxHostedUploadBytes) || maxHostedUploadBytes <= 0))) {
		return {kind: "none"};
	}
	const quote: RegularPriceQuote = {
		targetPlan: plan, currency, discount: null, renewal: minorToMajor(renewal, currency),
		maxHostedUploadBytes: maxHostedUploadBytes ?? null, isUpgrade: false,
	};
	if (discountsLeftToday === 0) return {kind: "regular", quote};
	if (!isWhole(firstMonth) || firstMonth < 0 || firstMonth > renewal) return {kind: "none"};
	const discount = discountOf(percentOff, amountOff, currency);
	if (discount === null) return {kind: "none"};
	return {
		kind: "coupon", discountsLeft: discountsLeftToday, withdrawn: false,
		gift: {...quote, discount, firstMonth: minorToMajor(firstMonth, currency)},
	};
}

function discountOf(percentOff: number | null | undefined, amountOff: number | null | undefined,
	currency: string): CouponGift["discount"] | null {
	if (percentOff != null && amountOff == null) {
		return isWhole(percentOff) && percentOff > 0 && percentOff <= 100 ? {kind: "percent", percentOff} : null;
	}
	if (amountOff != null && percentOff == null) {
		return isWhole(amountOff) && amountOff > 0 ? {kind: "amount", amountOff: minorToMajor(amountOff, currency)} : null;
	}
	return null;
}

/** What a Subscribe press starts: always the target plan, monthly; the token only while the coupon is live. */
export interface LimitWallCheckout {
	plan: PaidPlanId;
	period: Extract<BillingPeriod, "MONTHLY">;
	terms: {offerToken?: string};
}

/**
 * The checkout a press starts, or null when it must start none.
 *
 * <b>A live coupon carries exactly the link's token</b>, and the api attaches exactly the email's code or
 * refuses. <b>The full price (C7) is an ORDINARY checkout for the same plan, WITHOUT the token</b>: the
 * dialog already quoted the full price, and a token would only be refused. A withdrawn coupon starts
 * nothing, so a refusal is never silently turned into a full-price purchase.
 */
export function limitWallCheckout(presentation: LimitWallPresentation, token: string | null): LimitWallCheckout | null {
	if (presentation.kind === "regular") {
		return {plan: presentation.quote.targetPlan, period: "MONTHLY", terms: {}};
	}
	if (presentation.kind !== "coupon" || presentation.withdrawn || token === null) return null;
	return {plan: presentation.gift.targetPlan, period: "MONTHLY", terms: {offerToken: token}};
}

/**
 * Whether the dialog is on screen: a coupon or the full price, for a guest or a Free account, once the
 * session has settled for the first time, until this visit dismisses it. A paid account never sees it; the
 * api would refuse it.
 *
 * <b>The session gates the FIRST open only</b> ({@link sessionSettledOnce}), so a returning paid
 * subscriber never sees the offer flash. After that the plan alone decides, as `promoDialogOpen` does: a
 * guest's sign-in from the dialog's own Subscribe makes the session Pending again while the account is
 * read, and closing then would clear the consent the reader ticked while the checkout it resumes still
 * records it.
 *
 * Dismissal is a this-visit fact held in memory, like the promo link's: a reload of the still-live link
 * shows the dialog again.
 */
export function limitWallDialogOpen(presentation: LimitWallPresentation, user: User,
	settled: boolean, dismissed: boolean): boolean {
	return (presentation.kind === "coupon" || presentation.kind === "regular") && !dismissed
		&& settled && planOf(user) === "free";
}

/** Whether the session has answered at least once; a later Pending (a sign-in re-reading the account) keeps it. */
export function sessionSettledOnce(settledBefore: boolean, session: AuthenticatedSessionState): boolean {
	return settledBefore || session !== AuthenticatedSessionState.Pending;
}

/**
 * Subscribe through a sign-in: the checkout is decided at the moment it STARTS, from the presentation as
 * it is then, because a refusal can withdraw the coupon while the sign-in modal is up.
 */
export async function resumeLimitWallCheckout(ports: Omit<FreeCheckoutPorts, "start"> & {
	checkout: () => LimitWallCheckout | null;
	start: (checkout: LimitWallCheckout) => Promise<void>;
}): Promise<void> {
	await resumeFreeCheckout({...ports, start: async () => {
		const checkout = ports.checkout();
		if (checkout !== null) await ports.start(checkout);
	}});
}
